import React, { useEffect, useMemo } from "react";
import DateFormatter from "../../../../app/formatters/DateFormatter";
import { isScheduleGridAvailable } from "./RpaReservationStepScheduleGrid";

/** @typedef {import('../../services/RpaPlanningService.js').ModifiedRpaResourceItem } ModifiedRpaResourceItem */
/** @typedef {import('../../services/RpaPlanningService.js').RpaResourcesPerDayObj } RpaResourcesPerDayObj */

/**
 * @typedef {object} RpaReservationStepResourceProps
 * @property {Array<RpaResourcesPerDayObj>} planningDays - Journées du planning
 * @property {Array<number>} resourceIdsOrder - Ordre des ressources
 * @property {?number} selectedActivityId - Activité sélectionnée
 * @property {?number} selectedResourceId - Ressource sélectionnée
 * @property {function(number):void} onResourceChange - Sélection d'une ressource
 */

/**
 * Étape de sélection de la ressource
 * @param {RpaReservationStepResourceProps} props
 */
export default function RpaReservationStepResource({ planningDays, resourceIdsOrder, selectedActivityId, selectedResourceId, onResourceChange }) {
  const availableResources = useMemo(() => {
    return getResourcesFromPlanning(planningDays, resourceIdsOrder).filter(resource => Number(resource.rpaActivityId) === Number(selectedActivityId));
  }, [planningDays, resourceIdsOrder, selectedActivityId]);

  useEffect(() => {
    if (availableResources.length === 1 && Number(selectedResourceId) !== Number(availableResources[0].id)) {
      onResourceChange(Number(availableResources[0].id));
    }
  }, [availableResources, onResourceChange, selectedResourceId]);

  if (!selectedActivityId) {
    return null;
  }

  if (availableResources.length === 1) {
    return null;
  }

  return (
    <section>
      <div className="space--16"></div>
      <h3 className="title title--small title--no-margin">Terrain</h3>
      <div className="space--16"></div>
      <div className="grid grid--has-4-child grid--8-gap grid--has-2-child-mobile no-mobile">
        {availableResources.map(rpaResource => (
          <RpaResourceButton key={rpaResource.id} planningDays={planningDays} rpaResource={rpaResource} selected={Number(rpaResource.id) === Number(selectedResourceId)} onResourceChange={onResourceChange} />
        ))}
      </div>
      {!availableResources.length ? <p>Aucune ressource n'est disponible pour cette activité.</p> : null}
    </section>
  );
}

/**
 * Bouton de sélection d'une ressource
 * @param {{planningDays: Array<RpaResourcesPerDayObj>, rpaResource: ModifiedRpaResourceItem, selected: boolean, onResourceChange: function(number):void}} props
 */
function RpaResourceButton({ planningDays, rpaResource, selected, onResourceChange }) {
  const nextAvailableScheduleGrid = findNextAvailableScheduleGrid(planningDays, rpaResource.id);
  const buttonStyle = {
    padding: 0,
    gap: 0,
    overflow: "hidden"
  };

  return (
    <button type="button" className={`btn btn--small btn--full btn--two-labels ${selected ? "btn--main-color" : "btn--outline-grey"}`} style={buttonStyle} onClick={() => onResourceChange(Number(rpaResource.id))}>
      {rpaResource.thumbnail ? <img src={rpaResource.thumbnail} alt="" style={{ width: "100%", height: "100px", objectFit: "cover", borderRadius: "5px 5px 0 0", display: "block" }} /> : null}
      <span style={{ display: "flex", flexDirection: "column", gap: "2px", width: "100%", padding: "var(--spacing)" }}>
        <span className="btn__label--bold">{rpaResource.name}</span>
        {nextAvailableScheduleGrid ? <small className="btn__label--small">Prochain créneau disponible à {DateFormatter.formatHour(nextAvailableScheduleGrid.start_hour, true, true)}</small> : null}
      </span>
    </button>
  );
}

/**
 * Retourne le prochain créneau disponible d'une ressource
 * TU TODO 🔨
 * @param {Array<RpaResourcesPerDayObj>} planningDays - Journées du planning
 * @param {number} rpaResourceId - Identifiant de la ressource
 * @returns {?import('../../services/RpaPlanningService.js').ModifiedRpaScheduleGridOfCurrentDayItem}
 */
function findNextAvailableScheduleGrid(planningDays, rpaResourceId) {
  for (const day of planningDays) {
    const rpaResource = day.rpaResources.find(resource => Number(resource.id) === Number(rpaResourceId));
    const availableScheduleGrid = rpaResource?.scheduleGridsOfCurrentDay.find(rpaScheduleGrid => isScheduleGridAvailable(rpaResource, rpaScheduleGrid));

    if (availableScheduleGrid) {
      return availableScheduleGrid;
    }
  }

  return null;
}

/**
 * Retourne les ressources uniques du planning dans l'ordre du filtre
 * TU TODO 🔨
 * @param {Array<RpaResourcesPerDayObj>} planningDays - Journées du planning
 * @param {Array<number>} resourceIdsOrder - Ordre des ressources
 * @returns {Array<ModifiedRpaResourceItem>}
 */
function getResourcesFromPlanning(planningDays, resourceIdsOrder) {
  const resourcesById = new Map();

  planningDays.forEach(day => {
    day.rpaResources.forEach(resource => {
      if (!resourcesById.has(Number(resource.id))) {
        resourcesById.set(Number(resource.id), resource);
      }
    });
  });

  return resourceIdsOrder.reduce((resources, resourceId) => {
    const resource = resourcesById.get(resourceId);

    if (resource) {
      resources.push(resource);
    }

    return resources;
  }, /** @type {Array<ModifiedRpaResourceItem>} */ ([]));
}
